import React, { useCallback, useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import './OfficersPanel.css';
import { authStorage } from '../../utils/storage';
import { toTitleCase } from '../../utils/toTitleCase';
import { User, Phone, Home as HomeIcon, MapPin, Plus as PlusIcon, Trash2, Ban } from 'lucide-react';

const CORE_ROLE_ORDER = ['President', 'Vice President', 'Secretary', 'Treasurer'];
const CORE_ROLE_SET = new Set(CORE_ROLE_ORDER);
const normalizeRoleName = (pos = '') => {
    const raw = String(pos || '');
    const p = raw
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!p) return '';
    if (p === 'vice president' || p === 'vicepresident') return 'Vice President';
    if (p === 'president') return 'President';
    if (p === 'treasurer') return 'Treasurer';
    if (p === 'secretary') return 'Secretary';
    return raw.trim();
  };

const OfficersPanel = forwardRef(({ unitId, isEditMode }, ref) => {
  const [rows, setRows] = useState([]);
  const [rowsUnitId, setRowsUnitId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(new Set());
  const [dirtyFields, setDirtyFields] = useState({});
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [showRecon, setShowRecon] = useState(false);
  const [reconDirs, setReconDirs] = useState({}); // `${role}|${field}` -> true means Legacy → New
  const [showAdd, setShowAdd] = useState(false);
  const [statusRetryCount, setStatusRetryCount] = useState(0);
  const [newOfficer, setNewOfficer] = useState({ role: 'President', customRole: '', first_name: '', middle_name: '', last_name: '', email_primary: '', home_phone: '' });
  const ROLE_OPTIONS = ['President', 'Co-President', 'Vice President', 'Secretary', 'Treasurer', 'Trustee', 'Recording Secretary', 'Sergeant at Arms', 'Executive Board Member', 'Steward', 'Member at Large', 'Other…'];


  // Caches the on-screen card order so editing a title doesn't reshuffle cards;
  // re-derived only when the SET of rows changes (add/remove/reload).
  const orderRef = useRef({ sig: '', order: new Map() });
  const token = authStorage.getToken();
  const currentScope = useRef({ unitId, token });
  currentScope.current = { unitId, token };
  const rowsRequest = useRef(0);
  const getRowKey = (row) => (row?.assignment_id ?? row?.officer_id ?? null);

  const fetchData = useCallback(async () => {
    if (!token || currentScope.current.unitId !== unitId || currentScope.current.token !== token) return;
    const requestId = ++rowsRequest.current;
    const isCurrent = () => currentScope.current.unitId === unitId &&
      currentScope.current.token === token && rowsRequest.current === requestId;
    setRowsUnitId(null);
    setLoading(true);
    try {
      const res = await fetch(`/api/units/${unitId}/officers`, { headers: { Authorization: `Bearer ${token}` } });
      if (!isCurrent()) return;
      if (!res.ok) {
        const txt = await res.text();
        if (!isCurrent()) return;
        console.error('Officers API error:', res.status, txt);
        setError('Officers data not available yet.');
        setRows([]);
        return;
      }
      let data;
      try { data = await res.json(); } catch (e) { data = null; }
      if (!isCurrent()) return;
      if (!Array.isArray(data)) {
        console.warn('Unexpected officers payload:', data);
        setError('Officers data not available yet.');
        setRows([]);
      } else {
        setError(null);
        // Freeze each row's SAVED role. Legacy-merge and slot-occupancy logic
        // key off this, not the live-edited position_name, so renaming a title
        // in-place does not spawn/remove legacy placeholder cards.
        setRows(data.map(r => ({ ...r, __savedRole: normalizeRoleName(r.position_name) || (r.position_name || '') })));
        setRowsUnitId(unitId);
      }
    } catch (e) {
      if (!isCurrent()) return;
      console.error('Failed to load officers', e);
      setError('Failed to load officers');
    } finally { if (isCurrent()) setLoading(false); }
  }, [token, unitId]);

const fetchStatus = useCallback(async () => {
    if (!token) {
      setStatusLoading(false);
      return;
    }
    setStatusLoading(true);
    try {
      const res = await fetch(`/api/units/${unitId}/officers/status`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const txt = await res.text();
        console.warn('Officers status API error:', res.status, txt);
        setStatus(null);
        return;
      }
      let data = null;
      try { data = await res.json(); } catch (_) { data = null; }
      setStatus(data);
    } catch (e) {
      console.error('Failed to load officers status', e);
      setStatus(null);
    } finally { setStatusLoading(false); }
  }, [token, unitId]);

  useEffect(() => {
    setDirty(new Set());
    setDirtyFields({});
  }, [unitId]);

  // Expose saveAll method to parent component
  useImperativeHandle(ref, () => ({
    saveAll: async () => {
      if (!dirty.size) return true;
      try {
        const failed = new Set();
        const failedFieldMap = {};
        const dirtyKeys = Array.from(dirty);
        // Save dirty rows sequentially to keep it simple
        for (const key of dirtyKeys) {
          const idx = rows.findIndex((r) => String(getRowKey(r)) === String(key));
          if (idx < 0 || !rows[idx]) continue;
          try {
            await saveRow(idx);
          } catch (e) {
            console.error('Failed saving officer row', key, e);
            failed.add(String(key));
            if (dirtyFields[String(key)]) failedFieldMap[String(key)] = dirtyFields[String(key)];
          }
        }
        if (failed.size) {
          setDirty(failed);
          setDirtyFields(failedFieldMap);
          return false;
        }
        setDirty(new Set());
        setDirtyFields({});
        await fetchData();
        await fetchStatus();
        return true;
      } catch (e) {
        console.error('OfficersPanel saveAll failed', e);
        return false;
      }
    },
    discardChanges: async () => {
      if (!token) return false;
      try {
        setDirty(new Set());
        setDirtyFields({});
        setShowAdd(false);
        await fetchData();
        await fetchStatus();
        return true;
      } catch (e) {
        console.error('OfficersPanel discardChanges failed', e);
        return false;
      }
    },
  }));



  // Map a role name to a CSS-friendly badge class
  const roleClass = (pos = '') => {
    const p = String(pos || '').toLowerCase();
    // Specific variants first to avoid generic matches stealing them
    if (p.includes('vice')) return 'role-vice-president';
    if ((p.includes('executive') && p.includes('board')) || p.includes('e-board') || p.includes('e board') || p.includes('exec board') || p.includes('local executive board')) return 'role-exec-board';
    if (p.includes('record')) return 'role-recording-secretary';
    if (p.includes('member at large') || (p.includes('member') && p.includes('large'))) return 'role-member-at-large';
    if (p.includes('sergeant')) return 'role-sergeant';
    if (p.includes('steward')) return 'role-steward';
    if (p.includes('trustee')) return 'role-trustee';
    if (p.includes('treasurer')) return 'role-treasurer';
    if (p.includes('secretary')) return 'role-secretary';
    if (p.includes('president')) return 'role-president';
    return 'role-other';
  };


  useEffect(() => {
    if (!token) {
      setLoading(false);
      setStatusLoading(false);
      return;
    }
    fetchData();
    fetchStatus();
  }, [unitId, token, fetchData, fetchStatus]);

  // Auto-resolve duplicate core roles (President, Vice President, Secretary, Treasurer)
  const [dedupeTried, setDedupeTried] = useState(false);
  // Reset per-unit flags when navigating between units
  useEffect(() => {
    setDedupeTried(false);
    setStatusRetryCount(0);
  }, [unitId]);
  useEffect(() => {
    // A unit transition can retain the prior rows until the new request finishes.
    if (loading || rowsUnitId !== unitId || !rows || dedupeTried) return;
    const norm = (pos = '') => normalizeRoleName(pos) || null;
    const counts = {};
    for (const r of rows) {
      const role = norm(r.position_name);
      if (!role) continue;
      if (!CORE_ROLE_SET.has(role)) continue;
      counts[role] = (counts[role] || 0) + 1;
    }
    const hasDup = Object.values(counts).some(c => c > 1);
    if (!hasDup) return;
    (async () => {
      try {
        await fetch(`/api/units/${unitId}/officers/dedupe`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
        if (currentScope.current.unitId !== unitId || currentScope.current.token !== token) return;
        setDedupeTried(true);
        await fetchData();
        await fetchStatus();
      } catch (_) { /* ignore */ }
    })();
  }, [rows, rowsUnitId, loading, unitId, dedupeTried, token, fetchData, fetchStatus]);

  // NOTE: auto "ensure-baseline" seeding was removed. It POSTed a blank Officer
  // for any missing core role and fired on the initial (rows === []) mount before
  // data loaded, so reassigning a core officer out of their role spawned a blank
  // placeholder ("empty container") on reload. Empty roles now simply show no
  // card until an officer is added via "Add Role" (legacy still shows for
  // un-migrated roles). Baseline seeding, if ever needed, is an explicit action.





  // Retry status fetch when officer rows loaded but status request failed once.
  useEffect(() => {
    if (!token) return;
    if (loading || statusLoading) return;
    if (status) {
      if (statusRetryCount !== 0) setStatusRetryCount(0);
      return;
    }
    if (!Array.isArray(rows) || rows.length === 0) return;
    if (statusRetryCount >= 2) return;

    const t = setTimeout(async () => {
      setStatusRetryCount((c) => c + 1);
      await fetchStatus();
    }, 700);

    return () => clearTimeout(t);
  }, [token, unitId, loading, statusLoading, status, rows, statusRetryCount, fetchStatus]);

  // Merge current officer records with legacy values for display (prefer new values)
  const displayRows = useMemo(() => {
    const hasValue = (v) => v !== null && v !== undefined && String(v).length > 0;
    const list = [];
    const seenRoles = new Set();
    (Array.isArray(rows) ? rows : []).forEach((r, idx) => {
      const liveRole = normalizeRoleName(r.position_name) || (r.position_name || '');
      // Legacy merge + slot occupancy key off the SAVED role (frozen at load),
      // not the live-edited value — so renaming a title in-place never makes a
      // legacy placeholder card appear/disappear or reflow the list.
      const savedRole = (r.__savedRole !== undefined && r.__savedRole !== null && r.__savedRole !== '')
        ? r.__savedRole : liveRole;
      const role = liveRole; // displayed role (dropdown value) tracks the edit
      const legacy = savedRole && status?.legacyByRole ? (status.legacyByRole[savedRole] || {}) : {};
      const rowKey = getRowKey(r);
      const touchedByField = rowKey !== null && rowKey !== undefined ? (dirtyFields[String(rowKey)] || {}) : {};
      const shouldUseFallbackForField = (field) => !isEditMode || !touchedByField[field];
      const mergedField = (field, currentValue, legacyValue) => {
        if (hasValue(currentValue)) return currentValue;
        return shouldUseFallbackForField(field) ? (legacyValue || '') : (currentValue || '');
      };
      list.push({
        ...r,
        position_name: role || r.position_name,
        first_name: mergedField('first_name', r.first_name, legacy.first_name),
        last_name: mergedField('last_name', r.last_name, legacy.last_name),
        email_primary: mergedField('email_primary', r.email_primary, legacy.email),
        // Home phone can fall back from legacy if the home field has not been edited.
        home_phone: mergedField('home_phone', r.home_phone, legacy.phone),
        cell_phone: r.cell_phone || '',
        work_phone: r.work_phone || '',
        other_phone: r.other_phone || '',
        __idx: idx,
        __isLegacyOnly: false,
      });
      if (savedRole) seenRoles.add(savedRole);
    });

    const coreRoles = CORE_ROLE_ORDER;
    coreRoles.forEach(role => {
      const legacy = status?.legacyByRole ? status.legacyByRole[role] : null;
      const hasLegacy = legacy && ((legacy.first_name || legacy.last_name || legacy.email || legacy.phone));
      if (!hasLegacy) return;
      if (seenRoles.has(role)) return; // already have a new record for this role
      list.push({
        assignment_id: `legacy-${role}`,
        position_name: role,
        first_name: legacy.first_name || '',
        last_name: legacy.last_name || '',
        email_primary: legacy.email || '',
        home_phone: legacy.phone || '',
        __idx: null,
        __isLegacyOnly: true,
      });
    });

    // Natural role-based ordering: core roles (President, VP, Secretary,
    // Treasurer) first, then non-core alphabetically; new records before legacy.
    const naturalCompare = (a, b) => {
      const roleA = normalizeRoleName(a.position_name);
      const roleB = normalizeRoleName(b.position_name);
      const indexA = CORE_ROLE_ORDER.indexOf(roleA);
      const indexB = CORE_ROLE_ORDER.indexOf(roleB);
      const coreA = indexA !== -1;
      const coreB = indexB !== -1;

      if (coreA && coreB && indexA !== indexB) return indexA - indexB;
      if (coreA && !coreB) return -1;
      if (!coreA && coreB) return 1;

      // For the same role, prefer active/new records over legacy-only placeholders.
      if (!!a.__isLegacyOnly !== !!b.__isLegacyOnly) return a.__isLegacyOnly ? 1 : -1;

      // Non-core roles are sorted by role name, then original response order.
      const byRole = String(a.position_name || '').localeCompare(String(b.position_name || ''), undefined, { sensitivity: 'base' });
      if (byRole !== 0) return byRole;

      const idxA = Number.isInteger(a.__idx) ? a.__idx : Number.MAX_SAFE_INTEGER;
      const idxB = Number.isInteger(b.__idx) ? b.__idx : Number.MAX_SAFE_INTEGER;
      if (idxA !== idxB) return idxA - idxB;

      const keyA = String(a.assignment_id || a.officer_id || '');
      const keyB = String(b.assignment_id || b.officer_id || '');
      return keyA.localeCompare(keyB);
    };

    // Keep the on-screen order STABLE while editing. We only (re)derive the
    // role order when the SET of rows changes (add / remove / reload) — not when
    // a field such as the title is edited. Sorting on the live position_name
    // made cards jump around mid-edit; caching the order by row key fixes that.
    const keyOf = (x) => String(getRowKey(x) ?? x.assignment_id ?? `idx-${x.__idx}`);
    const sig = list.map(keyOf).slice().sort().join('|');
    if (orderRef.current.sig !== sig) {
      const order = new Map();
      [...list].sort(naturalCompare).forEach((it, i) => order.set(keyOf(it), i));
      orderRef.current = { sig, order };
    }
    const order = orderRef.current.order;
    return [...list].sort((a, b) => (order.get(keyOf(a)) ?? 0) - (order.get(keyOf(b)) ?? 0));
  }, [rows, status, isEditMode, dirtyFields]);

  function onFieldChange(idx, field, value) {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
    const key = getRowKey(rows[idx]);
    if (key !== null && key !== undefined) {
      setDirty(prev => {
        const next = new Set(prev);
        next.add(String(key));
        return next;
      });
      setDirtyFields(prev => ({
        ...prev,
        [String(key)]: {
          ...(prev[String(key)] || {}),
          [field]: true
        }
      }));
    }
  }

  async function saveRow(idx) {
    const r = rows[idx];
    // normalize names for consistency
    const first = toTitleCase(r.first_name || '');
    const middle = toTitleCase(r.middle_name || '');
    const last = toTitleCase(r.last_name || '');
    const body = {
      first_name: first,
      middle_name: middle,
      last_name: last,
      email_primary: r.email_primary,
      email_secondary: r.email_secondary,
      home_address: { line1: r.home_line1, line2: r.home_line2, city: r.home_city, state: r.home_state, zip: r.home_zip },
      mailing_address: { line1: r.mail_line1, line2: r.mail_line2, city: r.mail_city, state: r.mail_state, zip: r.mail_zip },
      phones: { home: r.home_phone, cell: r.cell_phone, work: r.work_phone, other: r.other_phone }
    };
    const res = await fetch(`/api/officers/${r.officer_id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body)
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Failed to update officer ${r.officer_id}: ${res.status} ${text}`);
    }
    // update assignment role/type if edited
    if ((r.assignment_id) && (typeof r.position_name === 'string') && r.position_name.trim()) {
      const roleRes = await fetch(`/api/officer-assignments/${r.assignment_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ position_name: r.position_name.trim() })
      });
      if (!roleRes.ok) {
        const text = await roleRes.text();
        throw new Error(`Failed to update assignment ${r.assignment_id}: ${roleRes.status} ${text}`);
      }
    }
    // update UI to normalized names immediately
    setRows(prev => prev.map((row, i) => i === idx ? { ...row, first_name: first, middle_name: middle, last_name: last } : row));
  }



  async function reconcileCopyMissing() {
    if (!token) return;
    try {
      await fetch(`/api/units/${unitId}/officers/reconcile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ mode: 'missing_only' })
      });
      // ignore body; refresh
      await fetchData();
      await fetchStatus();
    } catch (e) {
      console.error('Reconcile failed', e);
    }
  }

  // Initialize directions when status changes
  useEffect(() => {
    if (!status) return;
    const roles = CORE_ROLE_ORDER;
    const next = {};
    roles.forEach(role => {
      const l = status.legacyByRole?.[role] || {};
      const n = status.newByRole?.[role] || {};
      const pairs = [
        { key: 'name', l: `${(l.first_name || '').trim()} ${(l.last_name || '').trim()}`.trim(), n: `${(n.first_name || '').trim()} ${(n.last_name || '').trim()}`.trim() },
        { key: 'email', l: (l.email || '').trim(), n: (n.email || '').trim() },
        { key: 'phone', l: (l.phone || '').trim(), n: (n.home_phone || n.work_phone || n.cell_phone || '').trim() },
      ];
      pairs.forEach(p => {
        const id = `${role}|${p.key}`;
        // Default to New → Legacy (false). If New empty and Legacy has value, suggest Legacy → New (true)
        next[id] = (!p.n && !!p.l) ? true : false;
      });
    });
    setReconDirs(next);
  }, [status]);

  function toggleDir(id) {
    setReconDirs(prev => ({ ...prev, [id]: !prev[id] }));
  }

  function toggleAllDirs() {
    setReconDirs(prev => {
      const keys = Object.keys(prev);
      if (!keys.length) return prev;
      const allTrue = keys.every(k => !!prev[k]);
      const next = {};
      keys.forEach(k => next[k] = !allTrue);
      return next;
    });
  }

  async function applyReconcileDirections() {
    if (!status) return;
    const roles = CORE_ROLE_ORDER;
    // Optimistic update of officer grid while persisting
    const nextRows = [...rows];
    // Optimistic update for modal: preview new values in-place
    const nextStatus = status ? { ...status, newByRole: { ...(status.newByRole || {}) } } : null;
    const newReconDirs = { ...reconDirs };
    for (const role of roles) {
      const l = status.legacyByRole?.[role] || {};
      const n = status.newByRole?.[role] || {};
      const rows = [
        { key: 'name', legacy: `${(l.first_name || '').trim()} ${(l.last_name || '').trim()}`.trim(), current: `${(n.first_name || '').trim()} ${(n.last_name || '').trim()}`.trim() },
        { key: 'email', legacy: (l.email || '').trim(), current: (n.email || '').trim() },
        { key: 'phone', legacy: (l.phone || '').trim(), current: (n.home_phone || n.work_phone || n.cell_phone || '').trim() },
      ];
      for (const r of rows) {
        const id = `${role}|${r.key}`;
        if (reconDirs[id]) {
          // Legacy → New: perform copy
          await copyField(role, r.key);
          // Update local grid for immediate visual feedback
          const idx = nextRows.findIndex(x => normalizeRoleName(x.position_name) === role);
          if (idx >= 0) {
            if (r.key === 'name') {
              nextRows[idx] = { ...nextRows[idx], first_name: (l.first_name || '').trim(), last_name: (l.last_name || '').trim() };
            } else if (r.key === 'email') {
              nextRows[idx] = { ...nextRows[idx], email_primary: (l.email || '').trim() };
            } else if (r.key === 'phone') {
              nextRows[idx] = { ...nextRows[idx], home_phone: (l.phone || '').trim() };
            }
          }
          // Update modal preview status.newByRole
          if (nextStatus) {
            const cur = { ...(nextStatus.newByRole[role] || {}) };
            if (r.key === 'name') {
              cur.first_name = (l.first_name || '').trim();
              cur.last_name = (l.last_name || '').trim();
            } else if (r.key === 'email') {
              cur.email = (l.email || '').trim();
            } else if (r.key === 'phone') {
              cur.home_phone = (l.phone || '').trim();
            }
            nextStatus.newByRole[role] = cur;
          }
          // Reset arrow for this row (no further change pending)
          newReconDirs[id] = false;
        }
      }
    }
    setRows(nextRows);
    // Recompute mismatches so badges/rows reflect that differences are resolved
    if (nextStatus) {
      const sameStr = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
      const digits = (s) => String(s || '').replace(/\D+/g, '');
      const rolesList = CORE_ROLE_ORDER;
      const mismatches = [];
      const missingInNew = [];
      const missingInLegacy = [];
      rolesList.forEach(role => {
        const l = nextStatus.legacyByRole?.[role] || {};
        const n = nextStatus.newByRole?.[role] || {};
        if (l.first_name && !n.first_name) missingInNew.push({ role, field: 'first_name' });
        if (l.last_name && !n.last_name) missingInNew.push({ role, field: 'last_name' });
        if (l.email && !n.email) missingInNew.push({ role, field: 'email' });
        if (l.phone && !(n.home_phone || n.work_phone || n.cell_phone)) missingInNew.push({ role, field: 'phone' });
        // Mismatches when both present and differ
        const lName = `${String(l.first_name || '').trim()} ${String(l.last_name || '').trim()}`.trim();
        const nName = `${String(n.first_name || '').trim()} ${String(n.last_name || '').trim()}`.trim();
        if (l.first_name && l.last_name && n.first_name && n.last_name && !sameStr(lName, nName)) mismatches.push({ role, field: 'name' });
        if (l.email && n.email && !sameStr(l.email, n.email)) mismatches.push({ role, field: 'email' });
        const nPhone = n.home_phone || n.work_phone || n.cell_phone;
        if (l.phone && nPhone && digits(l.phone) !== digits(nPhone)) mismatches.push({ role, field: 'phone' });
      });
      nextStatus.mismatches = mismatches;
      nextStatus.missingInNew = missingInNew;
      nextStatus.missingInLegacy = missingInLegacy;
      setStatus(nextStatus);
    }
    setReconDirs(newReconDirs);
  }

  async function mergeAllOverwrite() {
    if (!token) return;
    try {
      await fetch(`/api/units/${unitId}/officers/reconcile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ mode: 'overwrite' })
      });
      await fetchStatus();
      await fetchData();
    } catch (e) {
      console.error('Merge overwrite failed', e);
    }
  }







  async function endAssignment(assignmentId) {
    if (!assignmentId) return;
    try {
      await fetch(`/api/officer-assignments/${assignmentId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      await fetchData(); await fetchStatus();
    } catch (e) { console.error('End assignment failed', e); }
  }

  async function deleteAssignment(assignmentId) {
    if (!assignmentId) return;
    if (!window.confirm('Delete this assignment permanently?')) return;
    try {
      await fetch(`/api/officer-assignments/${assignmentId}?hard=1`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      await fetchData(); await fetchStatus();
    } catch (e) { console.error('Delete assignment failed', e); }
  }

  async function createFromLegacy(role) {
    if (!status) return;
    const l = status.legacyByRole?.[role] || {};
    const pos = role;
    const first = (l.first_name || '').trim();
    const last = (l.last_name || '').trim();
    const email = (l.email || '').trim();
    const phone = (l.phone || '').trim();
    if (!first && !last && !email && !phone) return;
    try {
      await fetch(`/api/units/${unitId}/officers`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ position_name: pos, first_name: first, last_name: last, email_primary: email || null, phones: { home: phone || null } })
      });
      await fetchStatus();
      await fetchData();
    } catch (e) { console.error('Create from legacy failed', e); }
  }

  async function createMissingFromLegacy() {
    if (!status) return;
    const roles = CORE_ROLE_ORDER;
    for (const role of roles) {
      const l = status.legacyByRole?.[role] || {};
      const n = status.newByRole?.[role] || {};
      const hasLegacy = (l.first_name || l.last_name || l.email || l.phone);
      if (hasLegacy && !n.officer_id) {
        await createFromLegacy(role);
      }
    }
  }

  async function addOfficer() {
    if (!token) return;
    const role = newOfficer.role === 'Other…' ? (newOfficer.customRole || '').trim() : newOfficer.role;
    if (!role) return;
    try {
      const res = await fetch(`/api/units/${unitId}/officers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          position_name: role,
          first_name: (newOfficer.first_name || '').trim(),
          middle_name: (newOfficer.middle_name || '').trim(),
          last_name: (newOfficer.last_name || '').trim(),
          email_primary: (newOfficer.email_primary || '').trim(),
          phones: { home: (newOfficer.home_phone || '').trim() }
        })
      });
      if (!res.ok) {
        console.error('Add officer failed', await res.text());
      }
      setShowAdd(false);
      setNewOfficer({ role: 'President', customRole: '', first_name: '', middle_name: '', last_name: '', email_primary: '', home_phone: '' });
      await fetchData();
      await fetchStatus();
    } catch (e) {
      console.error('Add officer exception', e);
    }
  }

  async function copyField(role, field) {
    if (!status || !status.newByRole || !status.legacyByRole) return;
    const n = status.newByRole[role];
    const l = status.legacyByRole[role];
    if (!n || !n.officer_id) return;
    const officerId = n.officer_id;
    let body = {};
    if (field === 'name') {
      if (!l) return;
      body.first_name = (l.first_name || '').trim();
      body.last_name = (l.last_name || '').trim();
    } else if (field === 'email') {
      body.email_primary = (l?.email || '').trim();
    } else if (field === 'phone') {
      body.phones = { home: (l?.phone || '').trim() };
    }
    try {
      await fetch(`/api/officers/${officerId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      await fetchData();
      await fetchStatus();
    } catch (e) {
      console.error('Copy field failed', e);
    }
  }

  if (loading) return <div className="officers-panel">Loading leadership…</div>;
  if (error) return <div className="officers-panel compact-label">{error} — run migration 013 and/or import CSV to populate.</div>;

  return (
    <div className="officers-panel">
      <div className="officers-statusbar">
        {!statusLoading && status && (
          <>
            {!status.hasNew && (
              <span className="badge badge-error" title="No normalized leadership records for this unit yet">No leadership data</span>
            )}
            {status.hasLegacy && (
              <span className="badge badge-warn" title="Legacy officer fields exist in hidden sections">Legacy data present</span>
            )}
            {Array.isArray(status.mismatches) && status.mismatches.length > 0 && (
              <span className="badge badge-info" title="Legacy vs New values differ">{status.mismatches.length} mismatches</span>
            )}
          </>
        )}
        {false && isEditMode && status && status.hasLegacy && status.hasNew && (
          <>
            <button className="reconcile-btn" onClick={reconcileCopyMissing}>Copy Missing</button>
            <button className="reconcile-btn" onClick={mergeAllOverwrite}>Merge (Overwrite)</button>
          </>
        )}
        {isEditMode && status && status.hasLegacy && !status.hasNew && (
          <button className="reconcile-btn" onClick={createMissingFromLegacy}>Create Missing From Legacy</button>
        )}
        {status && (
          null
        )}
      </div>
      {isEditMode && (
        <div className="officers-toolbar">
          <button className="officers-saveall" style={{ background: '#3b82f6' }} onClick={() => setShowAdd(s => !s)}>{showAdd ? 'Cancel' : (<><PlusIcon size={16} style={{ marginRight: 6 }} />Add Role</>)}</button>
        </div>
      )}
      {isEditMode && showAdd && (
        <div className="officer-addform">
          <div className="add-row">
            <label>Role</label>
            <select className="officer-input" value={newOfficer.role} onChange={(e) => setNewOfficer(prev => ({ ...prev, role: e.target.value }))}>
              {ROLE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
            {newOfficer.role === 'Other…' && (
              <input className="officer-input" placeholder="Custom role" value={newOfficer.customRole} onChange={(e) => setNewOfficer(prev => ({ ...prev, customRole: e.target.value }))} />
            )}
          </div>
          <div className="add-row">
            <label>First</label>
            <input className="officer-input" value={newOfficer.first_name} onChange={(e) => setNewOfficer(prev => ({ ...prev, first_name: e.target.value }))} />
            <label>Middle</label>
            <input className="officer-input" value={newOfficer.middle_name} onChange={(e) => setNewOfficer(prev => ({ ...prev, middle_name: e.target.value }))} style={{ maxWidth: 140 }} />
            <label>Last</label>
            <input className="officer-input" value={newOfficer.last_name} onChange={(e) => setNewOfficer(prev => ({ ...prev, last_name: e.target.value }))} />
          </div>
          <div className="add-row">
            <label>Email</label>
            <input className="officer-input" value={newOfficer.email_primary} onChange={(e) => setNewOfficer(prev => ({ ...prev, email_primary: e.target.value }))} />
            <label>Home Phone</label>
            <input className="officer-input" value={newOfficer.home_phone} onChange={(e) => setNewOfficer(prev => ({ ...prev, home_phone: e.target.value }))} style={{ maxWidth: 220 }} />
          </div>
          <div className="add-actions">
            <button className="officers-saveall" onClick={addOfficer}>Create</button>
            <button className="reconcile-btn" onClick={() => setShowAdd(false)}>Close</button>
          </div>
        </div>
      )}
      {Array.isArray(displayRows) && displayRows.length === 0 ? (
        <div className="compact-label" style={{ padding: '8px 0' }}>No leadership data yet.</div>
      ) : null}
      <div className="officers-grid">
        {(Array.isArray(displayRows) ? displayRows : []).map((r, idx) => {
          const canEditRow = isEditMode && !r.__isLegacyOnly && typeof r.__idx === 'number';
          // Stable React key — must NOT include position_name, or renaming a
          // role would change the key and remount the card (flicker + focus loss).
          const key = r.assignment_id || r.officer_id || (typeof r.__idx === 'number' ? `row-${r.__idx}` : `legacy-${r.__savedRole || idx}`);
          return (
          <div className="officer-card" key={key}>
            <div className="officer-header">
              <div className="officer-name"><User size={16} className="icon-inline" /> {r.first_name} {r.middle_name || ''} {r.last_name}</div>
              {canEditRow ? (
                <div className="inline-actions">
                  <select
                    className="officer-input role-input role-select"
                    value={ROLE_OPTIONS.includes((r.position_name || '').trim()) ? (r.position_name || '').trim() : 'Other…'}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === 'Other…') return; // custom input will handle actual value
                      onFieldChange(r.__idx, 'position_name', val);
                    }}
                  >
                    {ROLE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                  {(!ROLE_OPTIONS.includes((r.position_name || '').trim())) && (
                    <input
                      className="officer-input role-input"
                      value={r.position_name || ''}
                      onChange={(e) => onFieldChange(r.__idx, 'position_name', e.target.value)}
                      placeholder="Custom role"
                    />
                  )}
                  <button className="icon-btn warning" title="End Term (Preserve History)" onClick={(e) => { e.stopPropagation(); endAssignment(r.assignment_id); }}><Ban size={16} /></button>
                  <button className="icon-btn danger" title="Delete Record (Permanent)" onClick={(e) => { e.stopPropagation(); deleteAssignment(r.assignment_id); }}><Trash2 size={16} /></button>
                </div>
              ) : (
                <span className={`officer-position ${roleClass(r.position_name)}`}>
                  {r.position_name}
                  {r.__isLegacyOnly && <span className="compact-label" style={{ marginLeft: 8 }}>Legacy data</span>}
                </span>
              )}
            </div>
            <div className="officer-sections">
              <div className="officer-section section-name-emails">
                <div className="section-title"><User size={14} className="section-icon" /> Name & Emails</div>
                {canEditRow ? (
                  <>
                    <div className="inline-group triple col-40-20-40 mb-8">
                      <input className="officer-input" placeholder="First" value={r.first_name || ''} onChange={e => onFieldChange(r.__idx, 'first_name', e.target.value)} />
                      <input className="officer-input" placeholder="Middle" value={r.middle_name || ''} onChange={e => onFieldChange(r.__idx, 'middle_name', e.target.value)} />
                      <input className="officer-input" placeholder="Last" value={r.last_name || ''} onChange={e => onFieldChange(r.__idx, 'last_name', e.target.value)} />
                    </div>
                    <div className="kv"><label>Primary Email</label><input className="officer-input" value={r.email_primary || ''} onChange={e => onFieldChange(r.__idx, 'email_primary', e.target.value)} /></div>
                    <div className="kv"><label>Secondary Email</label><input className="officer-input" value={r.email_secondary || ''} onChange={e => onFieldChange(r.__idx, 'email_secondary', e.target.value)} /></div>
                  </>
                ) : (
                  <>
                    <div className="kv"><label>Name</label><div className="value">{r.first_name} {r.middle_name || ''} {r.last_name}</div></div>
                    <div className="kv"><label>Primary Email</label><div className="value">{r.email_primary}</div></div>
                    <div className="kv"><label>Secondary Email</label><div className="value">{r.email_secondary}</div></div>
                  </>
                )}
              </div>
              <div className="officer-section section-phones">
                <div className="section-title"><Phone size={14} className="section-icon" /> Phones</div>
                {canEditRow ? (
                  <>
                    <div className="kv"><label>Home</label><input className="officer-input" value={r.home_phone || ''} onChange={e => onFieldChange(r.__idx, 'home_phone', e.target.value)} /></div>
                    <div className="kv"><label>Cell</label><input className="officer-input" value={r.cell_phone || ''} onChange={e => onFieldChange(r.__idx, 'cell_phone', e.target.value)} /></div>
                    <div className="kv"><label>Work</label><input className="officer-input" value={r.work_phone || ''} onChange={e => onFieldChange(r.__idx, 'work_phone', e.target.value)} /></div>
                  </>
                ) : (
                  <>
                    <div className="kv"><label>Home</label><div className="value phone">{r.home_phone}</div></div>
                    <div className="kv"><label>Cell</label><div className="value phone">{r.cell_phone}</div></div>
                    <div className="kv"><label>Work</label><div className="value phone">{r.work_phone}</div></div>
                  </>
                )}
              </div>
              <div className="officer-section section-home-address">
                <div className="section-title"><HomeIcon size={14} className="section-icon" /> Home Address</div>
                {canEditRow ? (
                  <>
                    <div className="kv"><label>Line 1</label><input className="officer-input" value={r.home_line1 || ''} onChange={e => onFieldChange(r.__idx, 'home_line1', e.target.value)} /></div>
                    <div className="kv"><label>Line 2</label><input className="officer-input" value={r.home_line2 || ''} onChange={e => onFieldChange(r.__idx, 'home_line2', e.target.value)} /></div>
                    <div className="inline-group triple col-50-20-30">
                      <input className="officer-input" placeholder="City" value={r.home_city || ''} onChange={e => onFieldChange(r.__idx, 'home_city', e.target.value)} />
                      <input className="officer-input" placeholder="State" value={r.home_state || ''} onChange={e => onFieldChange(r.__idx, 'home_state', e.target.value)} />
                      <input className="officer-input" placeholder="Zip" value={r.home_zip || ''} onChange={e => onFieldChange(r.__idx, 'home_zip', e.target.value)} />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="kv"><div className="value">{[r.home_line1, r.home_line2].filter(Boolean).join(' ')}</div></div>
                    <div className="kv"><div className="compact-label">{[r.home_city, r.home_state, r.home_zip].filter(Boolean).join(', ')}</div></div>
                  </>
                )}
              </div>
              <div className="officer-section section-mail-address">
                <div className="section-title"><MapPin size={14} className="section-icon" /> Mailing Address</div>
                {canEditRow ? (
                  <>
                    <div className="kv"><label>Line 1</label><input className="officer-input" value={r.mail_line1 || ''} onChange={e => onFieldChange(r.__idx, 'mail_line1', e.target.value)} /></div>
                    <div className="kv"><label>Line 2</label><input className="officer-input" value={r.mail_line2 || ''} onChange={e => onFieldChange(r.__idx, 'mail_line2', e.target.value)} /></div>
                    <div className="inline-group triple col-50-20-30">
                      <input className="officer-input" placeholder="City" value={r.mail_city || ''} onChange={e => onFieldChange(r.__idx, 'mail_city', e.target.value)} />
                      <input className="officer-input" placeholder="State" value={r.mail_state || ''} onChange={e => onFieldChange(r.__idx, 'mail_state', e.target.value)} />
                      <input className="officer-input" placeholder="Zip" value={r.mail_zip || ''} onChange={e => onFieldChange(r.__idx, 'mail_zip', e.target.value)} />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="kv"><div className="value">{[r.mail_line1, r.mail_line2].filter(Boolean).join(' ')}</div></div>
                    <div className="kv"><div className="compact-label">{[r.mail_city, r.mail_state, r.mail_zip].filter(Boolean).join(', ')}</div></div>
                  </>
                )}
              </div>
            </div>
            {r.__isLegacyOnly && isEditMode && (
              <div className="compact-label" style={{ marginTop: 8 }}>
                Legacy officer data shown for reference — add a new record to edit.
              </div>
            )}
          </div>
        );
        })}
      </div>
      {showRecon && status && (
        <div className="reconcile-modal-overlay" onClick={() => setShowRecon(false)}>
          <div className="reconcile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="reconcile-header">
              <h3>Reconcile Leadership Data</h3>
              <button className="modal-close" onClick={() => setShowRecon(false)}>×</button>
            </div>
            <div className="reconcile-body">
              <div className="recon-legend">
                <span className="pill">Legacy (old)</span>
                <span>→</span>
                <span className="pill">New (source of truth)</span>
                <span style={{ marginLeft: 10 }} className="compact-label">Green arrow means it will copy Legacy into New. Gray means no change.</span>
              </div>
              <table className="reconcile-table">
                <thead>
                  <tr>
                    <th>Role</th>
                    <th>Field</th>
                    <th>Legacy</th>
                    <th></th>
                    <th>New</th>
                  </tr>
                </thead>
                <tbody>
                  {CORE_ROLE_ORDER.map(role => {
                    const l = status.legacyByRole?.[role] || {};
                    const n = status.newByRole?.[role] || {};
                    const rows = [
                      { key: 'name', legacy: `${(l.first_name || '').trim()} ${(l.last_name || '').trim()}`.trim(), current: `${(n.first_name || '').trim()} ${(n.last_name || '').trim()}`.trim() },
                      { key: 'email', legacy: (l.email || '').trim(), current: (n.email || '').trim() },
                      { key: 'phone', legacy: (l.phone || '').trim(), current: (n.home_phone || n.work_phone || n.cell_phone || '').trim() },
                    ];
                    return rows.map(row => {
                      const id = `${role}|${row.key}`;
                      const dir = !!reconDirs[id];
                      return (
                        <tr key={`${role}-${row.key}`} className={`recon-row ${dir ? 'will-copy' : 'no-change'}`}>
                          <td>{role}</td>
                          <td>{row.key}</td>
                          <td className="legacy-cell">{row.legacy || <span className="compact-label">—</span>}</td>
                          <td className="arrow-cell">
                            {isEditMode && (
                              <button className={`arrow-btn ${dir ? 'copy' : 'no-change'}`} onClick={() => toggleDir(id)} title={dir ? 'Will copy Legacy into New' : 'No change'}>
                                {dir ? 'Copy Legacy → New' : 'No change'}
                              </button>
                            )}
                          </td>
                          <td className="current-cell">{row.current || <span className="compact-label">—</span>}
                            {isEditMode && !n.officer_id && row.key === 'name' && row.legacy && (
                              <div style={{ marginTop: 6 }}>
                                <button className="copy-btn" onClick={() => createFromLegacy(role)}>Create From Legacy</button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    });
                  })}
                </tbody>
              </table>
              {isEditMode && (
                <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button className="reconcile-btn" onClick={toggleAllDirs}>Toggle All</button>
                    <span className="compact-label">Default: New → Legacy. Flip arrows to import Legacy → New.</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="reconcile-btn" onClick={applyReconcileDirections}>Apply</button>
                    <button className="delete-btn" onClick={() => setShowRecon(false)}>Close</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default OfficersPanel;
